<script setup lang="ts">
/**
 * `</> Code` 源码面板：查看 / 复制 / 编辑并应用。
 *
 * 「应用」走 `sourceReplaceCommand`（整树替换）：**一条命令、可 Ctrl+Z 撤销**，
 * 且根元素身份不变（引擎的 ref、History、覆盖层都还指着它）。
 */
import { computed, ref, watch } from 'vue';
import { useEditor } from '@/composables/useEditor';
import { ui } from '@/stores/editor';

const editor = useEditor();
const editing = ref(false);
const draft = ref('');
const notice = ref('');

const current = computed(() => editor.sourceText.value);

const loadDraft = (): void => {
  draft.value = current.value;
  editing.value = false;
  notice.value = '';
};

watch(
  () => ui.codeOpen,
  (open) => {
    if (open) loadDraft();
  },
  { immediate: true },
);

// 文档变化时（未在编辑）自动跟随
watch(current, () => {
  if (ui.codeOpen && !editing.value) draft.value = current.value;
});

const apply = (): void => {
  if (editor.applySource(draft.value)) {
    editing.value = false;
    notice.value = '已应用（Ctrl+Z 可撤销）';
    return;
  }
  notice.value = '未应用：XML 解析失败，或与当前源码相同';
};

const copy = async (): Promise<void> => {
  try {
    await navigator.clipboard.writeText(editing.value ? draft.value : current.value);
    notice.value = '已复制到剪贴板';
  } catch {
    notice.value = '复制失败（浏览器未授权剪贴板）';
  }
};

const revert = (): void => {
  loadDraft();
  notice.value = '已还原为当前文档内容';
};
</script>

<template>
  <section v-if="ui.codeOpen" class="code-panel" aria-label="源码">
    <header class="code-head">
      <span class="panel-title">源码</span>
      <span class="grow" />
      <button class="ghost-btn" @click="copy">复制</button>
      <button class="ghost-btn" :class="{ 'is-active': editing }" @click="editing = !editing">
        {{ editing ? '取消编辑' : '编辑' }}
      </button>
      <button class="ghost-btn" :disabled="!editing" @click="apply">应用</button>
      <button class="ghost-btn" :disabled="!editing" @click="revert">还原</button>
      <button class="ghost-btn" title="关闭（Ctrl+E）" @click="ui.codeOpen = false">✕</button>
    </header>

    <textarea
      v-if="editing"
      class="code-area"
      data-field="code"
      spellcheck="false"
      :value="draft"
      @input="draft = ($event.target as HTMLTextAreaElement).value"
    />
    <pre v-else class="code-view" data-field="code-view">{{ current }}</pre>

    <p class="panel-note code-note">
      {{ notice || (editing ? '编辑中：应用后成为一条可撤销命令' : '只读视图 · 点「编辑」可直接改源码') }}
    </p>
  </section>
</template>
